import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebaseConfig';
import { ServiceType, DescriptionFormat } from '@/shared/types';
import { Button, Card, Input } from '@/components/ui';
import { Tooltip } from '@/components/ui/Tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { useToast } from '@/contexts/ToastContext';
import { useImageUpload } from '@/hooks/useImageUpload';
import { validateImageFile, validateCredits } from '@/utils/validation';
import { getServiceCost, getServiceIcon, getServiceName } from '@/utils/formatting';

import { InpaintingCanvas } from '@/components/InpaintingCanvas';

export function UnifiedUploadPanel(): React.JSX.Element {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { credits, storageUsed, storageLimit } = useCredits();
    const { showToast } = useToast();
    const { uploadImage, uploading } = useImageUpload();

    // State
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Item Removal State
    const [removalDescription, setRemovalDescription] = useState('');
    const [maskImage, setMaskImage] = useState<string | null>(null);

    // Virtual Staging State
    const [stagingStyle, setStagingStyle] = useState<string | null>(null);
    const [isCustomStagingOpen, setIsCustomStagingOpen] = useState(false);

    // Description State
    const [descriptionFormat, setDescriptionFormat] = useState<DescriptionFormat>(DescriptionFormat.CAPTION);
    const [propertyDetails, setPropertyDetails] = useState('');

    // Refs
    const fileInputRef = useRef<HTMLInputElement>(null);

    const containerRef = useRef<HTMLDivElement>(null);

    // Service Grid Order (3x2)
    const services = [
        ServiceType.ITEM_REMOVAL,
        ServiceType.STAGING,
        ServiceType.DESTAGING,
        ServiceType.DESCRIPTION,
        ServiceType.COLLAGE,
        ServiceType.DAY_TO_DUSK,
    ];

    // Staging Styles
    const quickStyles = ['Modern', 'Minimalist', 'Classic'];
    const customStyles = [
        'Contemporary', 'Industrial', 'Scandinavian', 'Bohemian',
        'Coastal', 'Farmhouse', 'Rustic', 'Art Deco',
        'Mediterranean', 'Transitional', 'Eclectic', 'Luxury',
        'Urban Loft', 'Tropical', 'Vintage'
    ];

    // Handlers
    const handleFileSelect = (selectedFile: File) => {
        console.log('handleFileSelect called', selectedFile.name, selectedFile.type, selectedFile.size);
        const error = validateImageFile(selectedFile);
        if (error) {
            console.log('Validation error:', error);
            showToast(error, 'error');
            return;
        }

        if (storageUsed + selectedFile.size > storageLimit) {
            showToast('Storage limit exceeded', 'error');
            return;
        }

        setFile(selectedFile);
        setPreviewUrl(URL.createObjectURL(selectedFile));
        // Reset state on new file
        setMaskImage(null);
        setRemovalDescription('');
        setStagingStyle(null);
        setPropertyDetails('');
        setDescriptionFormat(DescriptionFormat.CAPTION);
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };



    // Validation
    const isValid = () => {
        if (!file || !selectedService || isProcessing || uploading) return false;

        if (selectedService === ServiceType.ITEM_REMOVAL) {
            return !!maskImage;
        }

        if (selectedService === ServiceType.STAGING) {
            return !!stagingStyle;
        }

        if (selectedService === ServiceType.DESCRIPTION) {
            return !!propertyDetails;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!isValid() || !currentUser || !file || !selectedService) return;

        const cost = getServiceCost(selectedService);
        const creditError = validateCredits(credits, cost);
        if (creditError) {
            showToast(creditError, 'error');
            return;
        }

        setIsProcessing(true);

        try {
            // 1. Upload Main Image
            const uploadResult = await uploadImage(file);

            // 2. Upload Mask Image (if Item Removal)
            let maskImageUrl: string | undefined;
            if (selectedService === ServiceType.ITEM_REMOVAL && maskImage) {
                try {
                    // Convert base64 to Blob/File
                    const res = await fetch(maskImage);
                    const blob = await res.blob();
                    const maskFile = new File([blob], 'mask.png', { type: 'image/png' });

                    // Upload mask
                    const maskUploadResult = await uploadImage(maskFile);
                    maskImageUrl = maskUploadResult.downloadUrl;
                } catch (maskErr) {
                    console.error('Failed to upload mask:', maskErr);
                    showToast('Failed to upload mask image', 'error');
                    return;
                }
            }

            const createOrderFunc = httpsCallable(functions, 'createOrder');

            interface OrderRequest {
                serviceType: ServiceType;
                inputImageUrl: string;
                maskImageUrl?: string;
                removalPrompt?: string;
                stagingStyle?: string | null;
                descriptionFormat?: string;
                propertyDetails?: string;
            }

            const requestData: OrderRequest = {
                serviceType: selectedService,
                inputImageUrl: uploadResult.downloadUrl,
            };

            if (selectedService === ServiceType.ITEM_REMOVAL) {
                requestData.maskImageUrl = maskImageUrl;
                requestData.removalPrompt = removalDescription;
            } else if (selectedService === ServiceType.STAGING) {
                requestData.stagingStyle = stagingStyle;
            } else if (selectedService === ServiceType.DESCRIPTION) {
                requestData.descriptionFormat = descriptionFormat;
                requestData.propertyDetails = propertyDetails;
            }

            await createOrderFunc(requestData);
            showToast('Order processed successfully!', 'success');
            navigate('/gallery');

        } catch (error) {
            console.error('Order processing failed:', error);
            showToast('Failed to process order', 'error');
        } finally {
            setIsProcessing(false);
        }
    };



    const cost = selectedService ? getServiceCost(selectedService) : 0;

    return (
        <Card className="mb-8 flex flex-col">
            {/* 1. Canvas Area (Full Width) */}
            <div
                id="upload-area"
                ref={containerRef}
                className={`relative w-full h-[500px] bg-gray-100 transition-colors duration-200 flex items-center justify-center overflow-hidden rounded-t-lg ${isDragging ? 'bg-blue-50 border-2 border-blue-500 border-dashed' : ''
                    }`}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                {previewUrl ? (
                    <div className="relative w-full h-full flex items-center justify-center bg-gray-900">
                        <InpaintingCanvas
                            imageUrl={previewUrl}
                            enabled={selectedService === ServiceType.ITEM_REMOVAL}
                            onChange={setMaskImage}
                            className="w-full h-full"
                        />

                        <div className="absolute top-4 right-4 z-10">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setFile(null);
                                    setPreviewUrl(null);
                                    setMaskImage(null);
                                    setStagingStyle(null);
                                    setPropertyDetails('');
                                    setDescriptionFormat(DescriptionFormat.CAPTION);
                                }}
                            >
                                Change Image
                            </Button>
                        </div>

                        {selectedService === ServiceType.ITEM_REMOVAL && !maskImage && (
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded-full text-sm pointer-events-none">
                                Draw on the image to select items to remove
                            </div>
                        )}
                    </div>
                ) : (
                    <Tooltip content="Drag & drop your best property photo here." position="top">
                        <div
                            className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span className="text-6xl mb-4">📸</span>
                            <p className="text-lg font-medium text-gray-900">Drag & Drop Image</p>
                            <p className="text-sm text-gray-500 mt-2">or click to browse</p>
                            <p className="text-xs text-gray-400 mt-4">JPG, PNG, WEBP (Max 10MB)</p>
                        </div>
                    </Tooltip>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                        console.log('onChange fired', e.target.files?.length);
                        e.target.files?.[0] && handleFileSelect(e.target.files[0]);
                    }}
                />
            </div>

            {/* 2. Service Area (Bottom) */}
            <div className="p-6 bg-white border-t border-gray-200">

                {/* Service Grid (3x2) */}
                <div id="service-grid" className="grid grid-cols-3 gap-4 mb-6">
                    {services.map((service) => (
                        <Tooltip key={service} content={`Select ${getServiceName(service)}`} position="top">
                            <button
                                onClick={() => {
                                    if (service === ServiceType.COLLAGE) return;
                                    setSelectedService(service);
                                    // Reset sub-states when switching services
                                    if (service !== ServiceType.STAGING) setStagingStyle(null);
                                    if (service !== ServiceType.ITEM_REMOVAL) {
                                        setMaskImage(null);
                                        setRemovalDescription('');
                                    }
                                    if (service !== ServiceType.DESCRIPTION) {
                                        setPropertyDetails('');
                                        setDescriptionFormat(DescriptionFormat.CAPTION);
                                    }
                                }}
                                className={`w-full flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 h-32 ${selectedService === service
                                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md ring-1 ring-blue-500'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                    } ${service === ServiceType.COLLAGE ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                            >
                                <span className="text-3xl mb-2">{getServiceIcon(service)}</span>
                                <span className="font-medium text-sm text-center">{getServiceName(service)}</span>
                                <span className="text-xs text-gray-500 mt-1">{service === ServiceType.COLLAGE ? 'Coming Soon' : `${getServiceCost(service)} Credits`}</span>
                            </button>
                        </Tooltip>
                    ))}
                </div>

                {/* Dynamic Service Options */}

                {/* Virtual Staging Options */}
                {selectedService === ServiceType.STAGING && (
                    <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100 relative">
                        {/* Hint Card */}
                        <div className="absolute -top-3 -right-3 bg-yellow-100 border border-yellow-300 text-yellow-800 text-xs px-2 py-1 rounded shadow-sm z-10">
                            💡 Tip: Choose a style that matches the home's architecture.
                        </div>
                        <h4 className="text-sm font-bold text-blue-900 mb-3 uppercase tracking-wide">Select Staging Style</h4>
                        <div className="flex flex-wrap gap-3">
                            {/* Quick Select */}
                            {quickStyles.map((style) => (
                                <button
                                    key={style}
                                    onClick={() => setStagingStyle(style)}
                                    className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${stagingStyle === style
                                        ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300'
                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    {style}
                                </button>
                            ))}

                            {/* Extended Select (Custom) */}
                            <div className="relative">
                                <button
                                    onClick={() => setIsCustomStagingOpen(!isCustomStagingOpen)}
                                    className={`px-4 py-2 rounded-md font-medium text-sm transition-all flex items-center ${!quickStyles.includes(stagingStyle || '') && stagingStyle
                                        ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300'
                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    {(!quickStyles.includes(stagingStyle || '') && stagingStyle) ? stagingStyle : 'Custom'}
                                    <span className="ml-2 text-xs">▼</span>
                                </button>

                                {isCustomStagingOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-xl z-50 border border-gray-200 max-h-60 overflow-y-auto">
                                        {customStyles.map((style) => (
                                            <button
                                                key={style}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                                                onClick={() => {
                                                    setStagingStyle(style);
                                                    setIsCustomStagingOpen(false);
                                                }}
                                            >
                                                {style}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Item Removal Input */}
                {selectedService === ServiceType.ITEM_REMOVAL && (
                    <div className="mb-6 max-w-2xl mx-auto">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Describe items to remove (optional)
                        </label>
                        <Input
                            id="removal-description"
                            label="Description"
                            name="removalDescription"
                            value={removalDescription}
                            onChange={(val) => setRemovalDescription(val as string)}
                            placeholder="E.g., Remove the red car in the driveway..."
                        />
                    </div>
                )}

                {/* Description Options */}
                {selectedService === ServiceType.DESCRIPTION && (
                    <div className="mb-6 max-w-2xl mx-auto space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Description Format</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Choose the format that best suits your needs
                            </p>
                            <div className="space-y-3">
                                {[
                                    {
                                        value: DescriptionFormat.CAPTION,
                                        name: 'Caption',
                                        description: 'Brief social media caption (~150 characters)',
                                    },
                                    {
                                        value: DescriptionFormat.DESCRIPTION,
                                        name: 'Description',
                                        description: 'Marketing description (50-200 words)',
                                    },
                                    {
                                        value: DescriptionFormat.MLS_REMARKS,
                                        name: 'MLS Remarks',
                                        description: 'Comprehensive MLS-compliant remarks (max 999 characters)',
                                    },
                                ].map((format) => (
                                    <Card
                                        key={format.value}
                                        className={`cursor-pointer transition-all duration-200 hover:scale-102 hover:shadow-lg ${descriptionFormat === format.value ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                                            }`}
                                        onClick={() => setDescriptionFormat(format.value)}
                                    >
                                        <div className="flex items-start space-x-3">
                                            <div
                                                className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${descriptionFormat === format.value ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                                                    }`}
                                            >
                                                {descriptionFormat === format.value && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900">{format.name}</h4>
                                                <p className="text-sm text-gray-600">{format.description}</p>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Input
                                label="Property Details"
                                id="property-details"
                                name="propertyDetails"
                                type="textarea"
                                value={propertyDetails}
                                onChange={(val) => setPropertyDetails(val as string)}
                                placeholder="Add any specific details you want highlighted (bedrooms, features, location, etc.)"
                                required
                            />
                            {descriptionFormat === DescriptionFormat.MLS_REMARKS && (
                                <p className="text-sm text-gray-600 mt-1">
                                    Character limit: 999 characters
                                    {propertyDetails && (
                                        <span
                                            className={`ml-2 font-semibold ${propertyDetails.length > 999 ? 'text-red-600' : 'text-green-600'
                                                }`}
                                        >
                                            ({propertyDetails.length}/999)
                                        </span>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Action Bar */}
                <div id="action-bar" className="flex flex-col md:flex-row justify-between items-center pt-4 border-t border-gray-100">
                    <div className="mb-4 md:mb-0">
                        <span className="text-gray-600 mr-2">Total Cost:</span>
                        <span className="text-2xl font-bold text-gray-900">{cost} Credits</span>
                    </div>

                    <div className="w-full md:w-auto">
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-full md:w-64"
                            disabled={!isValid()}
                            onClick={handleSubmit}
                            loading={isProcessing || uploading}
                        >
                            {isProcessing || uploading ? 'Processing...' : 'Process Order'}
                        </Button>

                        {/* Validation Messages */}
                        {!isValid() && selectedService === ServiceType.ITEM_REMOVAL && file && !maskImage && (
                            <p className="text-xs text-red-500 text-center mt-2">
                                * Please select an area on the image
                            </p>
                        )}
                        {!isValid() && selectedService === ServiceType.STAGING && file && !stagingStyle && (
                            <p className="text-xs text-red-500 text-center mt-2">
                                * Please select a staging style
                            </p>
                        )}
                        {!isValid() && selectedService === ServiceType.DESCRIPTION && file && !propertyDetails && (
                            <p className="text-xs text-red-500 text-center mt-2">
                                * Please enter property details
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
}
